import { Camera } from 'domain/camera'
import { CameraHealthStatus } from 'features/cameras/types/camera-health.types'

export type CameraMapStatus =
  | 'inactive'
  | 'healthy'
  | 'silent'
  | 'never-detected'
  | 'unknown'

export interface CameraMapStatusInfo {
  status: CameraMapStatus
  label: string
  minutesSinceLastEvent: number | null
}

const formatSilentLabel = (minutes: number | null): string => {
  if (minutes == null) {
    return 'Sem detecção recente'
  }

  if (minutes < 60) {
    return `Sem detecção há ${Math.round(minutes)} min`
  }

  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return `Sem detecção há ${hours} h`
  }

  const days = Math.floor(hours / 24)
  return `Sem detecção há ${days} d`
}

/**
 * Traduz câmera + saúde (StreamHealthMonitor, via /camera/{clientId}/health)
 * para um status visual único no mapa. `isActive` (configuração) tem
 * prioridade sobre `isHealthy` (comportamento observado) — uma câmera
 * desativada de propósito não deve parecer "silenciosa"/em alerta.
 */
export const getCameraMapStatus = (
  camera: Pick<Camera, 'isActive'>,
  health: CameraHealthStatus | undefined
): CameraMapStatusInfo => {
  if (!camera.isActive) {
    return {
      status: 'inactive',
      label: 'Câmera desativada',
      minutesSinceLastEvent: null,
    }
  }

  if (!health) {
    return {
      status: 'unknown',
      label: 'Status indisponível',
      minutesSinceLastEvent: null,
    }
  }

  if (health.isHealthy) {
    return {
      status: 'healthy',
      label: 'Detectando normalmente',
      minutesSinceLastEvent: health.minutesSinceLastEvent,
    }
  }

  if (health.lastEventOverall) {
    return {
      status: 'silent',
      label: formatSilentLabel(health.minutesSinceLastEvent),
      minutesSinceLastEvent: health.minutesSinceLastEvent,
    }
  }

  return {
    status: 'never-detected',
    label: 'Nenhuma detecção registrada',
    minutesSinceLastEvent: null,
  }
}
